// Recover text-box (DrawingML + legacy VML) and OMML-equation text that never
// reaches the Plate fragment: mammoth structurally drops both (its docx reader
// has no textbox/math support), and even if raw markup leaked into mammoth's
// HTML output, sanitizeOfficeHtml strips every office-namespace tag before the
// HTML deserializer ever sees it. The existing OOXML fidelity pass
// (applyOoxmlProps) can't fill this gap either — it's anchor-based and
// ADDITIVE onto paragraphs mammoth already created; it never inserts new ones.
//
// So this is a separate recovery pre-pass: read document.xml directly and
// APPEND recovered content as plain paragraphs at the end of the fragment.
// Degraded by design — no formatting is recovered, and position is
// approximate (a floating text box has no reliable place in the flow).
// SmartArt/charts are out of scope: their content lives in separate package
// parts this pass never reads.
import { childByLocalName, childrenByLocalName, elementChildren, readDocxXml } from './readDocxXml';
import { normalizeText, runElements, runText } from './extract';

// The OMML ("math") namespace. m:r/m:t share their bare localNames ("r"/"t")
// with WML w:r/w:t — matching by namespace URI (not localName alone) keeps a
// math run's text from being confused with an ordinary paragraph run.
const OMML_NAMESPACE = 'http://schemas.openxmlformats.org/officeDocument/2006/math';

interface RecoverySource {
    kind: 'textbox' | 'math';
    el: Element;
}

// Walks the body collecting text-box paragraphs and OMML equations in
// document order. `mc:AlternateContent` wraps two serializations of the SAME
// drawing (modern DrawingML `wps:txbx` as Choice, legacy VML `v:textbox` as
// Fallback) — descending into both would recover the same text box twice, so
// only one branch (Choice, else Fallback) is walked.
function collectRecoverySources(el: Element, out: RecoverySource[]): void {
    for (const child of elementChildren(el)) {
        if (child.localName === 'AlternateContent') {
            const branch = childByLocalName(child, 'Choice') ?? childByLocalName(child, 'Fallback');
            if (branch) collectRecoverySources(branch, out);
            continue;
        }
        if ((child.localName === 'oMath' || child.localName === 'oMathPara') && child.namespaceURI === OMML_NAMESPACE) {
            out.push({ kind: 'math', el: child });
            continue;
        }
        // Both the modern DrawingML (w:drawing//wps:txbx/w:txbxContent) and
        // legacy VML (w:pict//v:textbox/w:txbxContent) shapes wrap the SAME
        // w:txbxContent element, whose own <w:p> paragraphs are ordinary
        // WordprocessingML — no need to special-case the wrapper elements.
        if (child.localName === 'txbxContent') {
            for (const p of childrenByLocalName(child, 'p')) out.push({ kind: 'textbox', el: p });
            continue;
        }
        collectRecoverySources(child, out);
    }
}

// Concatenates descendant m:t text, namespace-scoped (see OMML_NAMESPACE
// above). Naturally handles nested structures (m:oMathPara wrapping several
// m:oMath, fractions, subscripts, …) by recursing through every descendant.
function mathText(el: Element): string {
    let text = '';
    for (const child of elementChildren(el)) {
        text += child.namespaceURI === OMML_NAMESPACE && child.localName === 't' ? child.textContent ?? '' : mathText(child);
    }
    return text;
}

function textboxParagraphText(pEl: Element): string {
    const runEls: Element[] = [];
    runElements(pEl, runEls);
    return runEls.map(runText).join('');
}

function sourceToParagraph(source: RecoverySource): any | null {
    const raw = source.kind === 'math' ? mathText(source.el) : textboxParagraphText(source.el);
    const text = normalizeText(raw);
    return text ? { type: 'p', children: [{ text }] } : null;
}

function collectPlainText(nodes: any[]): string {
    let text = '';
    for (const node of nodes) {
        if (node == null) continue;
        if (typeof node.text === 'string') text += `${node.text} `;
        else if (Array.isArray(node.children)) text += collectPlainText(node.children);
    }
    return text;
}

// Per-block normalized text, for exact-match dedupe: each top-level fragment
// entry is one block (collectPlainText already flattens its own inline runs —
// links, marks — into one string, so a paragraph is compared as a whole, not
// split at each inline child), and a table's cells are additionally checked
// individually. Comparing whole blocks (not a substring-anywhere scan of the
// entire document) avoids dropping a short/generic recovered paragraph (e.g.
// a "DRAFT" watermark) just because that word also occurs inside an unrelated,
// longer paragraph elsewhere.
function collectBlockTexts(nodes: any[], out: Set<string>): void {
    for (const node of nodes) {
        if (node == null) continue;
        out.add(normalizeText(collectPlainText([node])));
        if (node.type === 'table' || node.type === 'tableWithoutHeaders') {
            for (const row of node.children ?? []) {
                for (const cell of row?.children ?? []) {
                    out.add(normalizeText(collectPlainText([cell])));
                }
            }
        }
    }
}

/**
 * Pure recovery core: given the already-parsed document.xml, appends
 * recovered text-box/OMML paragraphs to `fragment`. Exported separately from
 * the arrayBuffer wrapper below so it's unit-testable without a real .docx zip.
 */
export function recoverTextFromDocument(document: Document, fragment: any[]): any[] {
    const body = childByLocalName(document.documentElement, 'body');
    if (!body) return fragment;

    const sources: RecoverySource[] = [];
    collectRecoverySources(body, sources);
    if (sources.length === 0) return fragment;

    const recovered = sources.map(sourceToParagraph).filter((node): node is any => node != null);
    if (recovered.length === 0) return fragment;

    // Dedupe gate: a legacy VML text box's content occasionally survives
    // mammoth's own conversion (it reuses plain WordprocessingML paragraphs
    // internally), which would otherwise double it up here.
    const existingBlockTexts = new Set<string>();
    collectBlockTexts(fragment, existingBlockTexts);
    const deduped = recovered.filter((node) => !existingBlockTexts.has(node.children[0].text));
    return deduped.length ? [...fragment, ...deduped] : fragment;
}

/**
 * Best-effort arrayBuffer wrapper — mirrors applyOoxmlFidelity's contract: a
 * read/parse failure returns the fragment unchanged, so recovery can never
 * make an import worse than mammoth alone.
 */
export async function recoverLostText(arrayBuffer: ArrayBuffer, fragment: any[]): Promise<any[]> {
    try {
        const { document } = await readDocxXml(arrayBuffer);
        return recoverTextFromDocument(document, fragment);
    } catch {
        return fragment;
    }
}
